import { buttonStyle, OFF_SECONDARY_CLASS } from 'components/buttons/buttonStyle';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import { ChevronDownIcon, GlobeIcon, UsersIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from 'components/ui/popover';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { NEW_FOCUS_RING } from 'layout/NewDesignLayout/focusRing';
import { useId, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSetEmployeePickerAudienceMutation } from 'store/api/back-office-settings';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetJobRolesWithFiltersQuery } from 'store/api/jobRoles';
import type { EmployeePickerAudience, EmployeePickerElement, RequiredFieldScope } from 'types/back-office-settings';
import type { EmployeeListItemMinDto } from 'types/employees';
import type { JobRolesListElement } from 'types/jobRoles';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useSettingSave } from './useSettingSave';
import { AUDIENCE_TRIGGER_CLASS, ChoiceText, choiceClass } from './WhoHasItPopover';

const COPY = 'back-office-settings.employee-pickers';

/** The pickers hold what they select; a stored pick carries only its id and name. */
interface AudienceForm {
    scope: RequiredFieldScope;
    jobRoles: JobRolesListElement[];
    employees: EmployeeListItemMinDto[];
}

const storedAudience = (audience: EmployeePickerAudience): AudienceForm => ({
    scope: audience.scope,
    jobRoles: audience.jobRoles as JobRolesListElement[],
    employees: audience.employees.map(({ id, fullName }) => ({ id, name: fullName }) as EmployeeListItemMinDto),
});

interface Props {
    entry: EmployeePickerElement;
    /** The form and the field: "Contact · Relationship partner". */
    context: string;
    readOnly: boolean;
}

/**
 * A "Listed in employee pickers" row's chip and the popover it opens: the whole firm, or the colleagues whose current
 * job role is picked, or who are picked by name. Cancel drops the choice; Save writes the audience in one request.
 */
export function ListedInPopover({ entry, context, readOnly }: Props) {
    const intl = useIntl();
    const hintId = useId();
    const [open, setOpen] = useState(false);
    const [setAudience, { isLoading: isSaving }] = useSetEmployeePickerAudienceMutation();
    const save = useSettingSave();
    const { control, setValue, reset, getValues } = useForm<AudienceForm>({
        defaultValues: storedAudience(entry.audience),
    });
    const scope = useWatch({ control, name: 'scope' });
    const [jobRoles, employees] = useWatch({ control, name: ['jobRoles', 'employees'] });
    const picked = jobRoles.length + employees.length > 0;

    const { audience } = entry;
    const isFirm = audience.scope === 'TENANT';
    const count = audience.jobRoles.length + audience.employees.length;
    const shown = isFirm ? t('back-office-settings.whole-firm') : t(`${COPY}.chip-selected`, { count: `${count}` });

    const onOpenChange = (next: boolean) => {
        if (next) reset(storedAudience(audience));
        setOpen(next);
    };

    const onSave = async () => {
        const chosen = getValues();
        const saved = await save(() =>
            setAudience({
                form: entry.form,
                field: entry.field,
                scope: chosen.scope,
                jobRoleIds: chosen.jobRoles.map(({ id }) => id),
                employeeIds: chosen.employees.map(({ id }) => id),
            }).unwrap()
        );
        if (saved) setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverTrigger
                disabled={readOnly}
                aria-label={intl.formatMessage({ id: `${COPY}.chip-name` }, { shown, context })}
                className={cn(
                    AUDIENCE_TRIGGER_CLASS,
                    'hit-area relative h-7 cursor-pointer gap-1.5 ps-2.5 pe-2 text-[13px] [&_svg]:size-3.5',
                    NEW_FOCUS_RING
                )}
            >
                {isFirm ? <GlobeIcon /> : <UsersIcon />}
                {shown}
                <ChevronDownIcon />
            </PopoverTrigger>
            <PopoverContent
                align="end"
                sideOffset={8}
                positionerClassName="z-(--z-dropdown)"
                className="new:ring-border-menu ring-divider max-h-[min(var(--available-height),640px)] w-[440px] max-w-[calc(100vw-2rem)] gap-3 rounded-[10px] text-[13px]"
            >
                <div className="-mx-1 flex min-h-0 flex-col gap-3 overflow-y-auto overscroll-contain px-1">
                    <div className="flex flex-col gap-0.5">
                        <PopoverTitle className="text-fg text-sm font-semibold">
                            <FormattedMessage id={`${COPY}.listed-in`} />
                        </PopoverTitle>
                        <p className="text-grey-600 new:text-muted-foreground m-0 text-xs">{context}</p>
                    </div>
                    <RadioGroup
                        value={scope}
                        onValueChange={(value) => setValue('scope', value as RequiredFieldScope)}
                        aria-label={t(`${COPY}.listed-in`)}
                        className="gap-3"
                    >
                        <label className={cn(choiceClass(scope === 'TENANT'), 'cursor-pointer items-start')}>
                            <RadioGroupItem variant="primary" value="TENANT" className="mt-px" />
                            <ChoiceText
                                titleId="back-office-settings.whole-firm"
                                descriptionId={`${COPY}.whole-firm-description`}
                            />
                        </label>
                        <div className={cn(choiceClass(scope === 'SELECTED'), 'flex-col gap-3')}>
                            <label className="flex cursor-pointer items-start gap-2.5">
                                <RadioGroupItem variant="primary" value="SELECTED" className="mt-px" />
                                <ChoiceText
                                    titleId={`${COPY}.selected-only`}
                                    descriptionId={`${COPY}.selected-only-description`}
                                />
                            </label>
                            {scope === 'SELECTED' ? (
                                <div
                                    role="group"
                                    aria-label={t(`${COPY}.selected-only`)}
                                    aria-describedby={picked ? undefined : hintId}
                                    className="flex flex-col gap-2.5"
                                >
                                    <DynamicMultiSelectInput<AudienceForm, JobRolesListElement>
                                        control={control}
                                        setValue={setValue}
                                        name="jobRoles"
                                        label={`${COPY}.job-roles`}
                                        placeholder={`${COPY}.job-roles-placeholder`}
                                        paginated={{ fetch: useLazyGetJobRolesWithFiltersQuery, searchField: 'name' }}
                                        getOptionLabel="name"
                                        getOptionIdentifier="id"
                                    />
                                    <DynamicMultiSelectInput<AudienceForm, EmployeeListItemMinDto>
                                        control={control}
                                        setValue={setValue}
                                        name="employees"
                                        label={`${COPY}.colleagues`}
                                        placeholder="back-office-settings.add-colleague"
                                        paginated={{
                                            fetch: useLazyGetEmployeesListWithFiltersQuery,
                                            searchField: 'fullName',
                                        }}
                                        getOptionLabel="name"
                                        getOptionIdentifier="id"
                                    />
                                    {picked ? null : (
                                        <p id={hintId} className="text-fg-error new:text-danger-ink m-0 text-xs">
                                            <FormattedMessage id={`${COPY}.pick-one`} />
                                        </p>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    </RadioGroup>
                </div>
                <div className="flex shrink-0 justify-end gap-2">
                    <Button {...buttonStyle({ variant: 'outlined' }, 'h-8 px-3')} onClick={() => setOpen(false)}>
                        <FormattedMessage id="cancel" />
                    </Button>
                    <Button
                        {...buttonStyle(
                            { variant: 'contained', color: 'secondary' },
                            cn('h-8 px-3.5', OFF_SECONDARY_CLASS)
                        )}
                        disabled={isSaving || (scope === 'SELECTED' && !picked)}
                        focusableWhenDisabled
                        onClick={() => void onSave()}
                    >
                        <FormattedMessage id="save" />
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
